/**
 * TrafficLightLogo — CSS-based logo matching aitrafficlight.com
 *
 * Three animated dots (red/yellow/green) + "AI Traffic Light™" in mono font
 * + optional "powered by Guardrail Technologies™" subtext.
 *
 * @param size    - 'sm' | 'md' | 'lg' (default 'md')
 * @param showSub - show "powered by" subtext (default false)
 */

import { cn } from '@/lib/utils';

interface TrafficLightLogoProps {
  size?: 'sm' | 'md' | 'lg';
  showSub?: boolean;
  className?: string;
}

const DOT_SIZES = { sm: 'w-2 h-2', md: 'w-3 h-3', lg: 'w-4 h-4' } as const;
const TEXT_SIZES = { sm: 'text-xs', md: 'text-sm', lg: 'text-base' } as const;
const SUB_SIZES = { sm: 'text-[8px]', md: 'text-[10px]', lg: 'text-xs' } as const;
// Subtext left margin: 3 dots + 2 inner gaps (gap-1.5 = 0.375rem) + outer gap (gap-2 = 0.5rem)
// sm: 3×0.5rem + 2×0.375rem + 0.5rem = 2.75rem
// md: 3×0.75rem + 2×0.375rem + 0.5rem = 3.5rem
// lg: 3×1rem + 2×0.375rem + 0.5rem = 4.25rem
const SUB_MARGIN = { sm: 'ml-[2.75rem]', md: 'ml-[3.5rem]', lg: 'ml-[4.25rem]' } as const;

export function TrafficLightLogo({ size = 'md', showSub = false, className }: TrafficLightLogoProps) {
  const dot = DOT_SIZES[size];
  const text = TEXT_SIZES[size];
  const sub = SUB_SIZES[size];

  return (
    <div className={cn('flex flex-col', className)}>
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5">
          <span className={cn(dot, 'rounded-full bg-red-500')} />
          <span className={cn(dot, 'rounded-full bg-yellow-500')} />
          <span className={cn(dot, 'rounded-full bg-emerald-500')} />
        </div>
        <span className={cn(text, 'font-mono font-bold tracking-wider text-white')}>
          AI Traffic Light™
        </span>
      </div>
      {showSub && (
        <span className={cn(sub, 'font-mono text-zinc-500 tracking-normal', SUB_MARGIN[size])}>
          powered by Guardrail Technologies™
        </span>
      )}
    </div>
  );
}
